import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Users, BookOpen, Lock, CheckCircle, RotateCcw, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Story } from '../types/dualStory';
import { getDualStories } from '../services/dualStoryService';
import './DualStoryList.css';

const DualStoryList: React.FC = () => {
  const navigate = useNavigate();
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeline, setSelectedTimeline] = useState<'all' | 'modern' | 'ancient' | 'final'>('all');

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      // 从服务加载故事数据
      const storiesData = await getDualStories();

      // 从本地存储加载完成状态
      const savedProgress = localStorage.getItem('dualStoryProgress') || '{}';
      const progress = JSON.parse(savedProgress);
      const completedStories = progress.completedStories || [];

      // 检查主线剧情是否完成（现代线、古代线、王大爷梦境）
      const modernCompleted = completedStories.includes('1'); // 现代线故事ID
      const ancientCompleted = completedStories.includes('2'); // 古代线故事ID
      const wangCompleted = completedStories.includes('12'); // 王大爷故事ID

      // 更新故事的完成状态和解锁状态
      const storiesWithStatus = storiesData.map(story => {
        let unlocked = false;

        // 现代线和古代线默认解锁
        if (story.timeline === 'modern' || story.timeline === 'ancient') {
          unlocked = true;
        }
        // 王大爷梦境需要现代线和古代线都完成
        else if (story.id === '12') {
          unlocked = modernCompleted && ancientCompleted;
        }
        // 古今人性需要王大爷梦境完成
        else if (story.id === 'dual-insight') {
          unlocked = wangCompleted;
        }
        // 其他故事默认解锁
        else {
          unlocked = true;
        }

        return {
          ...story,
          completed: completedStories.includes(story.id),
          unlocked: unlocked
        };
      });

      setStories(storiesWithStatus);
    } catch (error) {
      console.error('加载故事失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 添加警魂传承作为特殊故事
  const mentorStory = {
    id: '67b8a1c2a3b4d5e6f7g8h9i0',
    surface: '警魂传承：新警的成长之路',
    difficulty: '5-10分钟',
    category: '对话互动',
    timeline: 'final' as const,
    tags: ['警队精神', '职业传承', '使命责任'],
    completed: false, // 可以根据需要调整
    unlocked: true,
    description: '一名新警的成长故事，在与老警官的对话中传承警队精神，理解肩上的责任与使命。',
    isMentorStory: true // 标记为导师故事
  };

  // 将警魂传承添加到故事列表中
  const allStories = [...stories, mentorStory];

  // 根据timeline和顺序排序
  const sortedStories = allStories.sort((a, b) => {
    // 王代线(1) → 古代线(2) → 王大爷(3) → 古今人性(4) → 警魂传承(5)
    const order = ['modern', 'ancient', 'final'];
    const aOrder = order.indexOf(a.timeline);
    const bOrder = order.indexOf(b.timeline);

    // 警魂传承总是最后
    if ((a as any).isMentorStory) return 1;
    if ((b as any).isMentorStory) return -1;

    // 王大爷和古今人性都是final timeline，需要区分
    if (a.timeline === 'final' && b.timeline === 'final') {
      // 王大爷（id=12）在古今人性（id=dual-insight）之前
      if (a.id === '12') return -1;
      if (b.id === '12') return 1;
      if (a.id === 'dual-insight') return -1;
      if (b.id === 'dual-insight') return 1;
    }

    return aOrder - bOrder;
  });

  const filteredStories = sortedStories.filter(story => {
    if ((story as any).isMentorStory) {
      // 警魂传承在"全部故事"和"新的篇章"中显示
      return selectedTimeline === 'all' || selectedTimeline === 'final';
    }
    return selectedTimeline === 'all' || story.timeline === selectedTimeline;
  });

  const getTimelineColor = (timeline: string) => {
    switch (timeline) {
      case 'modern':
        return 'from-blue-500 to-blue-700';
      case 'ancient':
        return 'from-amber-500 to-amber-700';
      case 'final':
        return 'from-purple-500 to-purple-700';
      default:
        return 'from-gray-500 to-gray-700';
    }
  };

  const getTimelineLabel = (timeline: string) => {
    switch (timeline) {
      case 'modern':
        return '现代线';
      case 'ancient':
        return '古代线';
      case 'final':
        return '新的篇章';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">加载故事中...</p>
        </div>
      </div>
    );
  }

  const completedCount = allStories.filter(s => s.completed).length;
  const totalCount = allStories.length;

  const handleResetProgress = () => {
    if (window.confirm('确定要重置所有主线剧情进度吗？这将清除所有已完成的章节记录。')) {
      // 清除本地存储的进度
      localStorage.removeItem('dualStoryProgress');

      // 重新加载页面以更新状态
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      {/* 导航栏 */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="flex justify-between items-center max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回主页
          </button>
          <h1 className="text-xl font-bold flex items-center">
            <BookOpen className="w-6 h-6 mr-2 text-purple-600" />
            双线叙事 - 主线剧情
          </h1>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-700">
              进度: {completedCount}/{totalCount}
            </div>
            <button
              onClick={handleResetProgress}
              className="flex items-center px-3 py-2 text-gray-600 hover:text-orange-600 transition"
              title="重置剧情进度"
            >
              <RotateCcw className="w-5 h-5 mr-1" />
              重置
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* 进度条 */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-lg font-semibold text-gray-800">整体进度</span>
            <span className="text-lg font-semibold text-purple-600">{Math.round((completedCount / totalCount) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(completedCount / totalCount) * 100}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </div>

        {/* 时线筛选 */}
        <div className="flex gap-2 mb-8">
          {[
            { key: 'all', label: '全部故事' },
            { key: 'modern', label: '现代线' },
            { key: 'ancient', label: '古代线' },
            { key: 'final', label: '新的篇章' }
          ].map((filter) => (
            <motion.button
              key={filter.key}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                selectedTimeline === filter.key
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setSelectedTimeline(filter.key as any)}
            >
              {filter.label}
            </motion.button>
          ))}
        </div>

        {/* 故事列表 */}
        <div className="grid md:grid-cols-2 gap-6">
          {filteredStories.map((story, index) => {
            // 判断是否为警魂传承故事
            const isMentorStory = (story as any).isMentorStory;

            return (
              <motion.div
                key={story.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`${
                  isMentorStory
                    ? 'bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl shadow-lg p-6 relative overflow-hidden border-2 border-orange-200'
                    : 'bg-white rounded-xl shadow-lg p-6 relative overflow-hidden'
                } ${!story.unlocked ? 'opacity-60' : ''} ${story.completed ? 'ring-2 ring-green-500' : ''}`}
              >
                {/* 顺序标记 */}
                <div className="absolute top-2 left-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                    story.timeline === 'modern' ? 'bg-blue-500' :
                    story.timeline === 'ancient' ? 'bg-amber-500' :
                    isMentorStory ? 'bg-orange-500' : 'bg-purple-500'
                  }`}>
                    {story.timeline === 'modern' ? '1' :
                     story.timeline === 'ancient' ? '2' :
                     story.timeline === 'final' && !isMentorStory && story.id === '12' ? '3' :
                     story.timeline === 'final' && !isMentorStory && story.id === 'dual-insight' ? '4' :
                     isMentorStory ? '5' : ''}
                  </div>
                </div>

                {/* 状态标记 */}
                {story.completed && (
                  <div className="absolute top-4 right-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                )}

                {/* 警魂传承特殊标记 */}
                {isMentorStory && (
                  <div className="absolute top-4 right-4">
                    <div className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      警魂传承
                    </div>
                  </div>
                )}

                {!story.unlocked && (
                  <div className="absolute top-4 right-4">
                    <Lock className="w-6 h-6 text-gray-400" />
                  </div>
                )}

                {/* 时线标签 */}
                <div className={`inline-block px-3 py-1 rounded-full text-sm text-white font-medium mb-4 bg-gradient-to-r ${getTimelineColor(story.timeline)}`}>
                  {getTimelineLabel(story.timeline)}
                </div>

                {/* 故事内容 */}
                <h3 className="text-xl font-bold mb-3 text-gray-800">{story.surface}</h3>

                {/* 警魂传承特殊描述 */}
                {isMentorStory && (
                  <p className="text-gray-600 mb-4">
                    {(story as any).description}
                  </p>
                )}

                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {story.difficulty}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {story.category}
                  </span>
                </div>

                {/* 标签 */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {story.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className={`px-2 py-1 text-xs rounded-full ${
                        isMentorStory && (tag === '警队精神' || tag === '职业传承' || tag === '使命责任')
                          ? tag === '警队精神' ? 'bg-orange-100 text-orange-700'
                            : tag === '职业传承' ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* 操作按钮 */}
                {story.unlocked ? (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full py-3 rounded-lg font-medium transition ${
                      isMentorStory
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600'
                        : story.completed
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                    onClick={() => {
                      if (isMentorStory) {
                        navigate('/mentor-story/67b8a1c2a3b4d5e6f7g8h9i0');
                      } else {
                        navigate(`/dual-story/${story.id}`);
                      }
                    }}
                  >
                    {story.completed ? '重新体验' : isMentorStory ? '开始传承' : '开始故事'}
                  </motion.button>
                ) : (
                  <div className="w-full py-3 rounded-lg bg-gray-100 text-gray-500 text-center font-medium">
                    需要完成前置故事
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* 提示信息 */}
        <div className="mt-12 p-6 bg-amber-50 border border-amber-200 rounded-lg">
          <h3 className="text-lg font-bold text-amber-800 mb-2">游戏提示</h3>
          <ul className="space-y-1 text-amber-700">
            <li>• 建议按照数字顺序完成故事：现代线(1) → 古代线(2) → 王大爷(3) → 警魂传承(4)</li>
            <li>• 两条故事线相互关联，注意寻找其中的联系</li>
            <li>• 每个故事都包含重要的法律知识点</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default DualStoryList;